<script setup lang="ts">
  import { computed, nextTick, onMounted, ref } from 'vue'
  import { useHomeChatStore } from '@/stores/home-chat'
  import { useSpeechToText } from '@/composables/useSpeechToText'
  import { useSnackbarStore } from '@/stores/snackbar'
  import { useUpgradeStore } from '@/stores/upgrade'
  import { useUserStore } from '@/stores/user'
  import { track } from '@/helpers/mixpanelDes'
  import { formatMonthlyResetDate, nextGenerationsPlan } from '@/helpers/planUsage'
  import { requiredPlanForBookFormat } from '@/helpers/product'
  import type { TOutputType } from '@/types/wordgenie-pro-state'
  import type { TDropdownOption } from '@/types/dropdown'
  import DesButton from '@/components/button/DesButton.vue'
  import DesButtonLink from '@/components/button/DesButtonLink.vue'
  import DesMessage from '@/components/DesMessage.vue'
  import DesContentNarrow from '@/components/DesContentNarrow.vue'
  import DesPill from '@/components/button/DesPill.vue'
  import DesHomeCreation from '@/components/home/DesHomeCreation.vue'

  const store = useHomeChatStore()
  const snack = useSnackbarStore()
  const upgrade = useUpgradeStore()
  const user = useUserStore()

  const textarea = ref<HTMLTextAreaElement | null>(null)

  const { isSupported, userWantsListening, toggleListening, stopListening } = useSpeechToText(
    undefined,
    (text) => (store.input += text),
  )

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      if (userWantsListening.value) stopListening()
      store.send()
    }
  }

  function handleMicClick() {
    if (!isSupported.value) {
      snack.add('Microphone is not available in your browser.', { type: 'danger' })
      return
    }
    toggleListening()
  }

  function handleClose() {
    store.clearError()
    textarea.value?.focus()
  }

  async function prefill(text: string) {
    store.clearError()
    store.input = text

    await nextTick()

    const el = textarea.value
    if (!el) return

    el.focus()
    el.selectionStart = el.selectionEnd = el.value.length
  }

  const pillOptions: Record<TOutputType, TDropdownOption> = {
    book: { name: 'book', label: 'Book', icon: 'fa-light fa-book' },
    presentation: { name: 'presentation', label: 'Presentation', icon: 'fa-light fa-presentation-screen' },
  }

  function chooseType(type: TOutputType) {
    if (store.loading) return

    if (type === 'presentation' && !user.canCreatePresentations()) {
      upgrade.showUpgradeModal('presentation-create', requiredPlanForBookFormat('presentation'))

      return
    }

    store.forcedType = type
    prefill(type === 'book' ? 'Create a book about ' : 'Create a presentation about ')
  }

  function clearType() {
    store.forcedType = null
  }

  const usage = computed(() => {
    const info = store.monthlyBooks
    if (!info?.result || info.unlimited || !info.limit) {
      return null
    }
    if (info.used >= info.limit) {
      return { blocked: true, left: 0, limit: info.limit }
    }
    if (info.used / info.limit >= 0.8) {
      return { blocked: false, left: info.limit - info.used, limit: info.limit }
    }

    return null
  })

  const resetsOn = formatMonthlyResetDate()

  function seeUpgradeOptions() {
    upgrade.showUpgradeModal('home-chat-limit', nextGenerationsPlan(store.monthlyBooks?.plan_name))
  }

  onMounted(() => {
    store.fetchMonthlyBooksInfo()
  })
</script>

<template>
  <DesContentNarrow>
    <div
      v-if="usage"
      class="flex items-center gap-4 -mb-4 rounded-t-xl bg-orange-95 px-4 pt-2.5 pb-6 text-sm text-orange-20"
    >
      <span>
        <span class="font-semibold">
          {{
            usage.blocked
              ? `You've used all ${usage.limit} free generations.`
              : `${usage.left} of ${usage.limit} free generations left.`
          }}
        </span>
        <span class="text-orange-30"> · Resets on {{ resetsOn }}</span>
      </span>

      <div class="grow"></div>

      <DesButtonLink class="!text-orange-20 !font-semibold" @click="seeUpgradeOptions">
        See upgrade options
      </DesButtonLink>
    </div>

    <div class="relative flex flex-col border-ai rounded-xl p-4 gap-3 transition-colors">
      <textarea
        ref="textarea"
        v-model="store.input"
        class="w-full min-h-20 text-base text-black-10 placeholder-black-60 outline-none bg-transparent resize-none field-sizing-content overflow-hidden"
        placeholder="What would you like to create today?"
        :disabled="store.loading"
        @keydown="handleKeydown"
        @input="store.clearError"
      ></textarea>

      <Transition
        enter-active-class="transition-[opacity,translate]"
        enter-from-class="opacity-0 -translate-y-1"
        leave-active-class="transition-[opacity,translate]"
        leave-to-class="opacity-0 -translate-y-1"
      >
        <DesMessage v-if="store.errorMessage" type="error" @close="handleClose">
          <span>
            {{ store.errorMessage }}
            <DesButtonLink class="text-red-50" v-if="store.canUpgrade" @click="upgrade.showUpgradeModal('home-chat')">
              Upgrade your account
            </DesButtonLink>
          </span>
        </DesMessage>
      </Transition>

      <div class="flex items-center gap-2">
        <div class="flex flex-wrap items-center gap-2 md:gap-4 text-sm">
          <Transition
            enter-active-class="transition-[opacity,translate]"
            enter-from-class="opacity-0 -translate-y-1"
            leave-active-class="transition-[opacity,translate]"
            leave-to-class="opacity-0 -translate-y-1"
          >
            <DesPill
              v-if="store.forcedType"
              :option="pillOptions[store.forcedType]"
              :color="store.forcedType === 'presentation' ? 'purple' : 'blue'"
              active
              @click="clearType"
            />
          </Transition>
          <DesButtonLink
            :to="`/creator/wordgenie/${store.forcedType ?? ''}`"
            @click="track('dashboard-link-clicked', { target: 'wordgenie-creator' })"
          >
            <img src="/icons/wordgenie-edit.svg" class="w-icon h-icon" alt="" />
            Use Standard Wordgenie
          </DesButtonLink>
        </div>

        <div class="grow"></div>

        <DesButton
          :type="userWantsListening ? 'red' : 'gray'"
          icon-only
          :click="handleMicClick"
          v-des-tooltip:bottom="userWantsListening ? 'Stop listening' : 'Voice input'"
        >
          <des-icon :icon="userWantsListening ? 'fa-solid fa-stop' : 'fa-light fa-microphone'" class="w-icon h-icon" />
        </DesButton>
        <DesButton
          type="ai"
          icon-only
          :disabled="!store.input.trim() || store.loading || usage?.blocked"
          :click="store.send"
        >
          <des-icon v-if="store.loading" icon="fa-solid fa-circle-notch" class="w-icon h-icon animate-spin" />
          <des-icon v-else icon="fa-solid fa-paper-plane" class="w-icon h-icon" />
        </DesButton>
      </div>
    </div>

    <Transition
      mode="out-in"
      enter-active-class="transition-[opacity,translate]"
      enter-from-class="opacity-0 -translate-y-1"
      leave-active-class="transition-[opacity,translate]"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div v-if="!store.forcedType" class="flex flex-wrap justify-center gap-3 mt-4">
        <DesPill
          v-for="(option, type) in pillOptions"
          :key="type"
          :option="{ ...option, label: `Create ${option.label.toLowerCase()}` }"
          :color="type === 'presentation' ? 'purple' : 'blue'"
          @click="chooseType(type)"
        />
      </div>

      <DesHomeCreation v-else class="mt-4" />
    </Transition>
  </DesContentNarrow>
</template>
